/* eslint-disable react-hooks/exhaustive-deps */

import React, { useEffect, useCallback, memo } from 'react';
import { Box, IconButton, Paper, Typography, CircularProgress, Tooltip, useTheme, useMediaQuery } from '@mui/material';
import ProvisionSelectorPopup from './popup/provision-selector-popup';
import ActionMenu from './action-menu';
import { DropdownOption } from './ui/dropdown';
import TreeDropdown from './tree-dropdown';
import AmountFilter from './filters/amount-filter';
import DescriptionFilter from './filters/description-filter';
import CategoryFilter from './filters/category-filter';
import DateFilter from './filters/date-filter';
import BankFilter from './filters/bank-filter';
import { Link, SlidersHorizontal, SquareCheckBig } from 'lucide-react';
import TablePlaceholder from './table-placeholder';
import { useSettings } from '../providers/settings-provider';
import { useUser } from '../providers/user-provider';
import { usePermissions } from '../providers/permissions-provider';
import { useNavigate } from 'react-router-dom';
import { reformatGstDescriptionDates } from '../util/date';

export type TransactionRow = {
    id: string;
    date: string;
    amount: number | string;
    balance: number | string;
    description: string;
    originalDescription?: string;
    bank: string;
    category: string;
    type: 'real' | 'provision';
    provisionId?: number | null;
    staffPaymentId?: number | null;
    isRecurring?: boolean;
    isProvisioned?: boolean;
    invoiceNumber?: string | null;
    linkedTransactionId?: string | null;
    provisionThisTemplateId?: number | null;
};

export type FilterParams = {
    sortBy: 'description' | 'category' | 'amount' | 'date' | 'bank' | undefined | null,
    sortDirection: 'ASC' | 'DESC',
    amountKeyword?: [number, number],
    descriptionKeyword?: string,
    searchKeyword?: string,
    categoryIds?: string[],
};

interface TransactionsTableProps {
    data: TransactionRow[];
    onEdit?: (row: TransactionRow) => void;
    onDelete?: (row: TransactionRow) => void;
    onLinkProvision?: (row: TransactionRow) => void;
    onProvisionThis?: (row: TransactionRow) => void;
    onMarkTransactions?: (transactionGuids: string[], provisionId?: number) => void;
    onProvisionThese?: (transactionGuids: string[]) => void;
    onCategoryChange?: (transactionId: string, categoryId: number | null) => void;
    highlightRows?: (row: TransactionRow, idx: number) => {
        color?: string;
        border?: string;
        background?: string;
        fontWeight?: number | string;
    };
    columns: { id: string; label: string; width: number | string }[];
    setColumnFilter: React.Dispatch<React.SetStateAction<FilterParams | undefined>>;
    columnFilter?: FilterParams | undefined;
    onRowClick?: (id: string, date: string, bank: string, category: string) => void;
    bankOptions: DropdownOption[];
    categoryOptions: DropdownOption[];
    hierarchicalCategories?: { id: string; label: string; children?: any[] }[];
    companyStatus?: {
        highestAmount?: number;
        lowestAmount?: number;
        latestTransaction?: Date | null;
        earliestTransaction?: Date | null;
        // New API fields (startOfRecords/endOfHorizon)
        startOfRecords?: { guid?: string | null; date?: Date | string | null } | null;
        endOfHorizon?: Date | string | null;
        // Optional cashflow info
        firstNegativeDate?: Date | string | null;
        firstNegativeTransactionGuid?: string | null;
        totalCount?: number;
        firstNegativeTransaction?: {
            balance?: number,
            date?: Date,
            guid?: string,
        },
    };
    stage?: 'initialize' | 'fetching' | 'complete';
    isHeaderFilterUsed?: boolean;
    startDate?: Date;
    endDate?: Date;
    viewBy?: string;
    // New setters/values so the date column filter can control header-level filters
    setViewBy?: (value: string) => void;
    unitValue?: string;
    setUnitValue?: (value: string) => void;
    isDateRange?: boolean;
    setIsDateRange?: (value: boolean) => void;
    setStartDate?: (value: Date) => void;
    setEndDate?: (value: Date) => void;
    hasNextPage?: boolean;
    onLoadMore?: () => void;
    isLoadingMore?: boolean;
    // Disables (not hides) row actions/inline edits - for a granular permission
    // (e.g. can('transactions', 'update')) in addition to the base admin check.
    canUpdate?: boolean;
    canDelete?: boolean;
}

// Memoized checkbox component to prevent unnecessary re-renders
const TransactionCheckbox = memo(({ 
    rowId, 
    isChecked, 
    isHovered, 
    hasAnyChecked, 
    onToggle 
}: { 
    rowId: string; 
    isChecked: boolean; 
    isHovered: boolean; 
    hasAnyChecked: boolean; 
    onToggle: (rowId: string) => void;
}) => {
    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        onToggle(rowId);
    }, [rowId, onToggle]);

    return (
        <Box display="flex" justifyContent="flex-end">
            <input
                type="checkbox"
                checked={isChecked}
                onChange={handleChange}
                style={{
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer',
                    accentColor: 'var(--color-primary)',
                    opacity: (isHovered || hasAnyChecked) ? 1 : 0.35,
                }}
            />
        </Box>
    );
});

TransactionCheckbox.displayName = 'TransactionCheckbox';

const TransactionsTable: React.FC<TransactionsTableProps> = ({ data, onEdit, onDelete, onLinkProvision, onProvisionThis, onMarkTransactions, onProvisionThese, onCategoryChange, highlightRows, columns, setColumnFilter, columnFilter, onRowClick, bankOptions, categoryOptions, hierarchicalCategories, companyStatus, stage = 'fetching', isHeaderFilterUsed, startDate, endDate, viewBy, setViewBy, unitValue, setUnitValue, isDateRange, setIsDateRange, setStartDate, setEndDate, hasNextPage, onLoadMore, isLoadingMore, canUpdate = true, canDelete = true }) => {
    const { formatDate, formatAmount } = useSettings();
    const { user } = useUser();
    // Server-verified per-entity admin flag - see table-virtual.tsx for why
    // User.role (login cookie) isn't used here.
    const { isAdmin } = usePermissions();
    const isReadOnly = !isAdmin;
    const editDisabled = isReadOnly || !canUpdate;
    const deleteDisabled = isReadOnly || !canDelete;
    const theme = useTheme();
    // This component's own dedicated grid layout (flex columns keyed off the
    // `columns` prop, sticky grouped headers, inline hover checkboxes) is
    // desktop-only - below this width it swaps to a simple card list instead
    // (see the isMobile branch below), same rationale as table-virtual.tsx.
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const amountRef = React.useRef<HTMLDivElement>(null);
    const descriptionRef = React.useRef<HTMLDivElement>(null);
    const categoryRef = React.useRef<HTMLDivElement>(null);
    const dateRef = React.useRef<HTMLDivElement>(null);
    const bankRef = React.useRef<HTMLDivElement>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const currentDayRef = React.useRef<HTMLDivElement>(null);
    const [activeFilterPopup, setActiveFilterPopup] = React.useState<string>('');
    const [hoveredRowId, setHoveredRowId] = React.useState<string | null>(null);
    const [checkedRows, setCheckedRows] = React.useState<Set<string>>(new Set());
    const [showProvisionSelector, setShowProvisionSelector] = React.useState(false);
    const [filterConfig, setFilterConfig] = React.useState<FilterParams>({
        sortBy: 'date',
        sortDirection: 'DESC'
    });

    const sortableColumns = columns.filter(col => ['date', 'amount', 'description', 'category', 'bank'].includes(col.id));

    // Refs to track scroll behavior
    const userHasScrolledRef = React.useRef(false);
    const isInitialLoadRef = React.useRef(true);

    // Memoized checkbox toggle handler
    const handleCheckboxToggle = useCallback((rowId: string) => {
        setCheckedRows(prev => {
            const newChecked = new Set(prev);
            if (newChecked.has(rowId)) {
                newChecked.delete(rowId);
            } else {
                newChecked.add(rowId);
            }
            return newChecked;
        });
    }, []);

    // Infinite scroll detection + track if user manually scrolls
    useEffect(() => {
        const container = containerRef.current;
        if (!container || !hasNextPage || isLoadingMore) return;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container;
            const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
            
            // Mark that user has manually scrolled
            userHasScrolledRef.current = true;
            
            // Load more when user has scrolled 80% of the content
            if (scrollPercentage > 0.8 && hasNextPage && !isLoadingMore && onLoadMore) {
                onLoadMore();
            }
        };

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [hasNextPage, isLoadingMore, onLoadMore]);

    // Reset checkboxes when company changes
    useEffect(() => {
        setCheckedRows(new Set());
    }, [user?.entityId]);

    // Initialize filterConfig from columnFilter prop when it changes
    useEffect(() => {
        if (columnFilter) {
            // If sortBy is null (filters cleared), use date as default
            setFilterConfig({
                ...columnFilter,
                sortBy: columnFilter.sortBy || 'date'
            });
        } else {
            // Initialize with defaults if no columnFilter
            setFilterConfig({
                sortBy: 'date',
                sortDirection: 'DESC'
            });
        }
    }, [columnFilter]);

    // Reset manual scroll flag when data changes (new query result)
    useEffect(() => {
        userHasScrolledRef.current = false;
        isInitialLoadRef.current = true;
    }, [data]);

    // Auto-scroll to current day only on initial load (not when filters change)
    useEffect(() => {
        if (stage === 'complete' && containerRef.current && isInitialLoadRef.current && !userHasScrolledRef.current) {
            // Mark initial load as complete
            isInitialLoadRef.current = false;
            
            // Use a longer timeout to ensure DOM has updated
            setTimeout(() => {
                const element = currentDayRef.current;
                const container = containerRef.current;
                if (element && container) {
                    const elementTop = element.offsetTop;
                    const elementHeight = element.offsetHeight;
                    const containerHeight = container.clientHeight;
                    const scrollPosition = elementTop - (containerHeight / 1.25) + (elementHeight / 2);
                    container.scrollTop = scrollPosition;
                }
            }, 200);
        }
    }, [stage]);

    // Only sync back to parent when user actually applies a filter, not on every change
    const handleApply = (params: Partial<FilterParams>) => {
        const newConfig = {
            ...filterConfig,
            ...params
        };
        setFilterConfig(newConfig);
        setColumnFilter(newConfig); // Only call this when user applies changes
        setActiveFilterPopup('');
    }

    const getColumnDisplayState = (colId: string) => {
        const isFilterActive = () => {
            switch (colId) {
                case 'amount':
                    return !!filterConfig.amountKeyword;
                case 'description':
                    return !!filterConfig.descriptionKeyword;
                case 'category':
                    return !!filterConfig.categoryIds?.length;
                default:
                    return false;
            }
        };

        const isSortActive = filterConfig.sortBy === colId;
        const isPopupOpen = activeFilterPopup === colId;

        return {
            isFilterActive: isFilterActive(),
            isSortActive,
            isPopupOpen
        };
    };

    const filters = {
        amount: (
            <AmountFilter
                anchorEl={amountRef.current}
                open={activeFilterPopup === 'amount'}
                onClose={() => setActiveFilterPopup('')}
                min={companyStatus?.lowestAmount || 0}
                max={companyStatus?.highestAmount || 999999}
                onApply={handleApply}
                currentSort={{
                    sortBy: filterConfig.sortBy || undefined,
                    sortDirection: filterConfig.sortDirection
                }}
                currentAmountKeyword={filterConfig.amountKeyword}
            />
        ),
        description: (
            <DescriptionFilter
                anchorEl={descriptionRef.current}
                open={activeFilterPopup === 'description'}
                onClose={() => setActiveFilterPopup('')}
                onApply={handleApply}
                currentSort={{
                    sortBy: filterConfig.sortBy || undefined,
                    sortDirection: filterConfig.sortDirection
                }}
                currentDescriptionKeyword={filterConfig.descriptionKeyword}
            />
        ),
        category: (
            <CategoryFilter
                anchorEl={categoryRef.current}
                open={activeFilterPopup === 'category'}
                onClose={() => setActiveFilterPopup('')}
                onApply={handleApply}
                currentSort={{
                    sortBy: filterConfig.sortBy || undefined,
                    sortDirection: filterConfig.sortDirection
                }}
            />
        ),
        date: (
            <DateFilter
                anchorEl={dateRef.current}
                open={activeFilterPopup === 'date'}
                onClose={() => setActiveFilterPopup('')}
                onApply={handleApply}
                currentSort={{
                    sortBy: filterConfig.sortBy || undefined,
                    sortDirection: filterConfig.sortDirection
                }}
                viewBy={viewBy}
                setViewBy={setViewBy}
                unitValue={unitValue}
                setUnitValue={setUnitValue}
                isDateRange={isDateRange}
                setIsDateRange={setIsDateRange}
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                companyStatus={companyStatus}
            />
        ),
        bank: (
            <BankFilter
                anchorEl={bankRef.current}
                open={activeFilterPopup === 'bank'}
                onClose={() => setActiveFilterPopup('')}
                onApply={handleApply}
                currentSort={{
                    sortBy: filterConfig.sortBy || undefined,
                    sortDirection: filterConfig.sortDirection
                }}
            />
        ),
    };

    const handleFilterClick = (colId: string) => {
        if (['amount', 'description', 'category', 'date', 'bank'].includes(colId)) {
            // Handle filter popup opening
            if (activeFilterPopup === colId) {
                setActiveFilterPopup('');
            } else {
                setActiveFilterPopup(colId);
            }
        } else {
            // Handle other column sorting
            const newDirection = filterConfig.sortBy === colId && filterConfig.sortDirection === 'ASC' ? 'DESC' : 'ASC';
            setFilterConfig(prev => ({
                ...prev,
                sortBy: colId as any,
                sortDirection: newDirection
            }));
        }
    };

    if (isMobile) {
        if (stage !== 'complete') {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4, flexDirection: 'column', gap: 2 }}>
                    <CircularProgress />
                    <Typography sx={{ ml: 2 }} fontSize={16}>
                        {stage === 'initialize' ? 'Initializing filters...' : 'Loading Transactions...'}
                    </Typography>
                </Box>
            );
        }
        if (!data || data.length === 0) {
            return (
                <TablePlaceholder
                    title="No Transactions Found"
                    message={(!companyStatus?.totalCount || companyStatus?.totalCount === 0) && !companyStatus?.earliestTransaction
                        ? "Get started by importing transactions or creating categories."
                        : `You have ${companyStatus?.totalCount || 0} transactions and provisions from ${companyStatus?.earliestTransaction ? formatDate(companyStatus.earliestTransaction) : 'N/A'} to ${companyStatus?.latestTransaction ? formatDate(companyStatus.latestTransaction) : 'N/A'}. Adjust your filters to view results.`}
                    latestTransaction={companyStatus?.latestTransaction}
                    earliestTransaction={companyStatus?.earliestTransaction}
                    totalCount={companyStatus?.totalCount}
                />
            );
        }
        return (
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                {data.map((row, idx) => {
                    const isProvisioning = row.type === 'provision';
                    const amount = Number(row.amount);
                    const amountColor = amount < 0 ? 'var(--color-negative-amount)' : 'var(--color-default-text)';
                    const bankLabel = String(bankOptions.find(option => String(option.value) === String(row.bank))?.label || row.bank || '');
                    const categoryLabel = String(categoryOptions.find(option => String(option.value) === String(row.category))?.label || row.category || '');
                    const paperBgColor = isProvisioning
                        ? (idx % 2 === 0 ? 'var(--color-provision-row-bg)' : 'rgba(var(--color-provision-row-bg-rgb), 0.5)')
                        : (idx % 2 === 0 ? 'var(--color-transaction-row-bg)' : 'rgba(var(--color-transaction-row-bg-rgb), 0.5)');
                    const borderColor = isProvisioning ? '#ffe5f0' : '#e0ecf7';
                    return (
                        <Paper
                            key={row.id}
                            onClick={() => onRowClick?.(row.id, row.date, row.bank, row.category)}
                            sx={{
                                p: 1.25,
                                borderRadius: 1,
                                background: paperBgColor,
                                border: `1px solid ${borderColor}`,
                                boxSizing: 'border-box',
                                cursor: onRowClick ? 'pointer' : 'default',
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#707070' }}>
                                        {reformatGstDescriptionDates(row.description, formatDate)}
                                    </Typography>
                                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: isProvisioning ? 'var(--color-provision-text)' : 'var(--color-transaction-text)' }}>
                                        {formatDate(new Date(row.date))}
                                    </Typography>
                                </Box>
                                <Box onClick={(e) => e.stopPropagation()}>
                                    <ActionMenu
                                        actions={[
                                            ...(onEdit ? [{ label: 'Edit', onClick: () => onEdit(row), icon: 'edit' as const, disabled: editDisabled }] : []),
                                            ...(onDelete ? [{ label: 'Delete', onClick: () => onDelete(row), icon: 'delete' as const, disabled: deleteDisabled }] : []),
                                        ]}
                                    />
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#707070' }}>Amount</Typography>
                                <Typography sx={{ fontSize: 12, fontWeight: isProvisioning ? 700 : 600, color: amountColor }}>{formatAmount?.(amount)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#707070' }}>Balance</Typography>
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: Number(row.balance) < 0 ? 'var(--color-negative-amount)' : 'var(--color-default-text)' }}>
                                    {row.balance === null || row.balance === undefined || row.balance === '' ? '-' : formatAmount?.(Number(row.balance))}
                                </Typography>
                            </Box>
                            {bankLabel && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#707070' }}>Bank</Typography>
                                    <Typography sx={{ fontSize: 12 }}>{bankLabel}</Typography>
                                </Box>
                            )}
                            {categoryLabel && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#707070' }}>Category</Typography>
                                    <Typography sx={{ fontSize: 12 }}>{categoryLabel}</Typography>
                                </Box>
                            )}
                        </Paper>
                    );
                })}
                {isLoadingMore && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, py: 2 }}>
                        <CircularProgress size={20} />
                        <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Loading...</Typography>
                    </Box>
                )}
            </Box>
        );
    }

    return (
        <Paper sx={{ width: '100%', borderRadius: 0, background: 'var(--color-page-background)', p: 4, boxSizing: 'border-box' }}>
            <Box 
                sx={{ 
                    display: 'flex', 
                    background: 'var(--color-container-background)', 
                    py: 2, 
                    borderRadius: 2, 
                    justifyContent: 'space-between',
                    position: 'sticky', 
                    top: 82, 
                    zIndex: 15
                }}
            >
                {/* Grouped headers: Date, Amount, Balance */}
                <Box sx={{ display: 'flex', gap: 0, alignItems: 'center' }}>
                {columns.filter(col => ['date', 'amount', 'balance'].includes(col.id)).map(col => (
                    <Box 
                        key={col.id} 
                        sx={{
                            width: col.width, 
                            height: 30,
                            fontWeight: 600, 
                            fontSize: 15,
                            textAlign: col.id === 'date' ? 'right' : 'right',
                            display: 'flex',
                            pl: (col.id === 'date' || col.id === 'amount') ? 2 : 0,
                            pr: col.id === 'amount' || col.id === 'balance' ? 2 : 0,
                            alignItems: 'center',
                            justifyContent: viewBy !== 'week' ? 'flex-end' : 'center',
                            boxSizing: 'border-box'
                        }}
                    >
                        <span ref={col.id === 'amount' ? amountRef : col.id === 'date' ? dateRef : null}>{col.label}</span>
                        { sortableColumns.find(c => c.id === col.id) &&
                            <IconButton
                                size="small"
                                style={{ marginLeft: 4, color: 'var(--color-primary)', height: 24 }}
                                onClick={handleFilterClick.bind(null, col.id)}
                                tabIndex={-1}
                                aria-label="Sort"
                            >
                                {(() => {
                                    const { isFilterActive, isSortActive, isPopupOpen } = getColumnDisplayState(col.id);
                                    
                                    return <Box display="flex" alignItems="center" gap={0.5}>
                                        { isFilterActive &&
                                            <SlidersHorizontal size={12} />
                                        }
                                        <Typography style={{ 
                                            opacity: (isSortActive || (isPopupOpen && col.id === 'date')) ? 0.9 : 0.3, 
                                            color: isSortActive ? 'var(--color-primary)' : '#707070ff',
                                            fontSize: 12 
                                        }}>
                                            {(isSortActive || (isPopupOpen && col.id === 'date')) 
                                                ? (filterConfig.sortDirection === 'ASC' ? '▲' : '▼') 
                                                : '▼'
                                            }
                                        </Typography>
                                    </Box>;
                                })()}
                            </IconButton>
                        }
                        {filters[col.id as keyof typeof filters]}
                    </Box>
                ))}
                </Box>

                {/* Remaining headers: Description, Bank, Category, Actions */}
                {columns.filter(col => !['date', 'amount', 'balance'].includes(col.id)).map(col => (
                    <Box 
                        key={col.id} 
                        sx={{
                            width: col.width, 
                            height: 30,
                            fontWeight: 600, 
                            fontSize: 15,
                            textAlign: col.id === 'date' ? 'left' : col.id === 'amount' || col.id === 'balance' ? 'left' : col.id === 'actions' ? 'right' : 'center',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: col.id === 'actions' ? 'flex-end' : 'flex-start',
                            paddingRight: col.id === 'actions' ? 2 : 0,
                        }}
                    >
                        <span ref={col.id === 'description' ? descriptionRef : col.id === 'category' ? categoryRef : col.id === 'bank' ? bankRef : null}>{col.label}</span>
                        {col.id === 'actions' && checkedRows.size > 0 && (
                            <Box sx={{ ml: 1 }}>
                                <ActionMenu actions={[
                                    { 
                                        label: (() => {
                                            const selectableRows = data.filter(row => !row.provisionThisTemplateId && row.id !== 'current-day-placeholder' && row.type === 'real');
                                            return checkedRows.size === selectableRows.length ? 'Unselect All' : 'Select All';
                                        })(),
                                        onClick: () => {
                                            const selectableRows = data.filter(row => !row.provisionThisTemplateId && row.id !== 'current-day-placeholder' && row.type === 'real');
                                            if (checkedRows.size === selectableRows.length) {
                                                setCheckedRows(new Set());
                                            } else {
                                                setCheckedRows(new Set(selectableRows.map(row => row.id)));
                                            }
                                        }, 
                                        icon: (() => {
                                            const selectableRows = data.filter(row => !row.provisionThisTemplateId && row.id !== 'current-day-placeholder' && row.type === 'real');
                                            return checkedRows.size === selectableRows.length ? 'close' : 'check';
                                        })()
                                    },
                                    { 
                                        label: 'Relate to Provision', 
                                        onClick: () => {
                                            setShowProvisionSelector(true);
                                        }, 
                                        icon: 'link' 
                                    },
                                    ...(checkedRows.size >= 4 ? [{ 
                                        label: 'Provision These', 
                                        onClick: () => {
                                            const selectedGuids = Array.from(checkedRows);
                                            if (onProvisionThese) {
                                                onProvisionThese(selectedGuids);
                                            }
                                            setCheckedRows(new Set());
                                        }, 
                                        icon: 'paste' 
                                    }] : [])
                                ]} />
                            </Box>
                        )}
                        { sortableColumns.find(c => c.id === col.id) &&
                            <IconButton
                                size="small"
                                style={{ marginLeft: 4, color: 'var(--color-primary)', height: 24 }}
                                onClick={handleFilterClick.bind(null, col.id)}
                                tabIndex={-1}
                                aria-label="Sort"
                            >
                                {(() => {
                                    const { isFilterActive, isSortActive, isPopupOpen } = getColumnDisplayState(col.id);
                                    
                                    return <Box display="flex" alignItems="center" gap={0.5}>
                                        { isFilterActive &&
                                            <SlidersHorizontal size={12} />
                                        }
                                        <Typography style={{ 
                                            opacity: (isSortActive || (isPopupOpen && col.id === 'date')) ? 0.9 : 0.3, 
                                            color: isSortActive ? 'var(--color-primary)' : '#707070ff',
                                            fontSize: 12 
                                        }}>
                                            {(isSortActive || (isPopupOpen && col.id === 'date')) 
                                                ? (filterConfig.sortDirection === 'ASC' ? '▲' : '▼') 
                                                : '▼'
                                            }
                                        </Typography>
                                    </Box>;
                                })()}
                            </IconButton>
                        }
                        {filters[col.id as keyof typeof filters]}
                    </Box>
                ))}
            </Box>
            {stage !== 'complete' ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4, flexDirection: 'column', gap: 2 }}>
                    <CircularProgress />
                    <Typography sx={{ ml: 2 }} fontSize={16}>
                        {stage === 'initialize' ? 'Initializing filters...' : 'Loading Transactions...'}
                    </Typography>
                </Box>
            ) : (((!data || (Array.isArray(data) && data.length === 0)) && stage === 'complete')
                ?
                    <TablePlaceholder
                        title="No Transactions Found"
                        message={(!companyStatus?.totalCount || companyStatus?.totalCount === 0) && !companyStatus?.earliestTransaction 
                            ? "Get started by importing transactions or creating categories." 
                            : `You have ${companyStatus?.totalCount || 0} transactions and provisions from ${companyStatus?.earliestTransaction ? formatDate(companyStatus.earliestTransaction) : 'N/A'} to ${companyStatus?.latestTransaction ? formatDate(companyStatus.latestTransaction) : 'N/A'}. Adjust your filters to view results.`}
                        latestTransaction={companyStatus?.latestTransaction}
                        earliestTransaction={companyStatus?.earliestTransaction}
                        totalCount={companyStatus?.totalCount}
                    />
                : 
                <div ref={containerRef} style={{ maxHeight: 'calc(100vh - 230px)', overflowY: 'auto' }}>
                {(() => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const hasCurrentDay = data.some(row => {
                        const rowDate = new Date(row.date);
                        rowDate.setHours(0, 0, 0, 0);
                        return rowDate.getTime() === today.getTime();
                    });

                    let processedData = [...data];
                    
                    // Add blank row for current day if it doesn't exist (only for transactions view)
                    if (!hasCurrentDay && viewBy === 'transactions' && (filterConfig.sortBy === null || filterConfig.sortBy === 'date')) {
                        // Format today's date properly in local timezone
                        const year = today.getFullYear();
                        const month = String(today.getMonth() + 1).padStart(2, '0');
                        const day = String(today.getDate()).padStart(2, '0');
                        const todayStr = `${year}-${month}-${day}`;
                        
                        const blankRow: TransactionRow = {
                            id: 'current-day-placeholder',
                            date: todayStr,
                            amount: '',
                            balance: '',
                            description: '',
                            bank: '',
                            category: '',
                            type: 'real'
                        };
                        
                        // Find correct insertion point
                        if (data.length === 0) {
                            processedData.push(blankRow);
                        } else {
                            // Determine sort order by comparing first two dates
                            const firstDate = new Date(data[0].date);
                            firstDate.setHours(0, 0, 0, 0);
                            const secondDate = data.length > 1 ? new Date(data[1].date) : null;
                            if (secondDate) secondDate.setHours(0, 0, 0, 0);
                            
                            const isDescending = secondDate ? firstDate.getTime() >= secondDate.getTime() : false;
                            
                            let inserted = false;
                            if (isDescending) {
                                // Data is sorted newest first - find first date < today
                                for (let i = 0; i < processedData.length; i++) {
                                    const rowDate = new Date(processedData[i].date);
                                    rowDate.setHours(0, 0, 0, 0);
                                    if (rowDate.getTime() < today.getTime()) {
                                        processedData.splice(i, 0, blankRow);
                                        inserted = true;
                                        break;
                                    }
                                }
                            } else {
                                // Data is sorted oldest first - find first date > today
                                for (let i = 0; i < processedData.length; i++) {
                                    const rowDate = new Date(processedData[i].date);
                                    rowDate.setHours(0, 0, 0, 0);
                                    if (rowDate.getTime() > today.getTime()) {
                                        processedData.splice(i, 0, blankRow);
                                        inserted = true;
                                        break;
                                    }
                                }
                            }
                            
                            // If we haven't inserted yet, add to end
                            if (!inserted) {
                                processedData.push(blankRow);
                            }
                        }
                    }
                    
                    // Helper component for range indicators
                    const RangeIndicator = ({ type, label }: { type: 'red' | 'orange', label: string }) => (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: 30,
                                width: '100%',
                                background: type === 'red' ? '#ffe5e5' : '#fff5e5',
                                pl: 2,
                                fontSize: 12,
                                fontWeight: 500,
                                color: type === 'red' ? '#cc0000' : '#cc7700',
                                boxSizing: 'border-box'
                            }}
                        >
                            {label}
                        </Box>
                    );
                    
                    const isSortAsc = filterConfig?.sortDirection === 'ASC';
                    
                    // Helper function to check if row contains a transaction date within its range
                    // Handles year-only (YYYY), date ranges (YYYY-MM-DD to YYYY-MM-DD), and single dates
                    const rowContainsDate = (dateStr: string, comparisonDate: Date | null | undefined): boolean => {
                        if (!dateStr || !comparisonDate) return false;
                        
                        const compDate = new Date(comparisonDate);
                        compDate.setHours(0, 0, 0, 0);
                        
                        // Handle year-only format (e.g., "2025")
                        if (/^\d{4}$/.test(dateStr)) {
                            return Number(dateStr) === compDate.getFullYear();
                        }
                        
                        // Handle date range format (e.g., "2025-01-01 to 2025-12-31")
                        // Check if the comparison date falls within this range
                        if (dateStr.includes(' to ')) {
                            const [start, end] = dateStr.split(' to ');
                            const startDate = new Date(start);
                            const endDate = new Date(end);
                            startDate.setHours(0, 0, 0, 0);
                            endDate.setHours(23, 59, 59, 999);
                            return compDate >= startDate && compDate <= endDate;
                        }
                        
                        // Handle single date format
                        return new Date(dateStr).toDateString() === comparisonDate.toDateString();
                    };
                    
                    // Check first/last records and compare against earliest/latest and the new startOfRecords
                    const firstRecord = processedData[0];
                    const lastRecord = processedData[processedData.length - 1];

                    // Prefer startOfRecords (GUID preferred; date fallback) when deciding "Start of Records"
                    const startOfRecordsObj = companyStatus?.startOfRecords;
                    const startOfRecordsGuid = startOfRecordsObj?.guid || null;
                    const startOfRecordsDate = startOfRecordsObj?.date ? new Date(startOfRecordsObj.date) : null;

                    const isFirstRecordStartOfRecords = firstRecord && startOfRecordsObj && (
                        (startOfRecordsGuid && firstRecord.id === startOfRecordsGuid) || rowContainsDate(firstRecord.date, startOfRecordsDate)
                    );

                    const isLastRecordStartOfRecords = lastRecord && startOfRecordsObj && (
                        (startOfRecordsGuid && lastRecord.id === startOfRecordsGuid) || rowContainsDate(lastRecord.date, startOfRecordsDate)
                    );

                    // Determine top/bottom indicator color and label
                    // New rule: if the active date-range `endDate` extends beyond
                    // `companyStatus.endOfHorizon`, show the "End of Horizon" text
                    // (labels still respect sort order; color remains "orange" when driven by filters).
                    const horizonDate = companyStatus?.endOfHorizon ? new Date(companyStatus.endOfHorizon) : null;
                    const filterEndBeyondHorizon = endDate && horizonDate && endDate.getTime() > horizonDate.getTime();

                    // Top indicator (depends on sort direction)
                    let topIndicatorType: 'red' | 'orange' = 'red';
                    let topLabel = 'Start of records';

                    if (isSortAsc) {
                        if (isFirstRecordStartOfRecords) {
                            topIndicatorType = 'red';
                            topLabel = 'Start of Records';
                        } else {
                            topIndicatorType = 'orange';
                            topLabel = 'Beginning of filter results';
                        }
                    } else {
                        if (filterEndBeyondHorizon) {
                            // Only show End of Horizon when the user's filter end passes the horizon
                            topIndicatorType = 'red';
                            topLabel = 'End of Horizon';
                        } else {
                            topIndicatorType = 'orange';
                            topLabel = 'End of filter results';
                        }
                    }

                    // Bottom indicator (depends on sort direction)
                    let bottomIndicatorType: 'red' | 'orange' = 'red';
                    let bottomLabel = 'End of Horizon';
                    if (isSortAsc) {
                        if (filterEndBeyondHorizon) {
                            // Only show End of Horizon when the user's filter end passes the horizon
                            bottomIndicatorType = 'red';
                            bottomLabel = 'End of Horizon';
                        } else {
                            bottomIndicatorType = 'orange';
                            bottomLabel = 'End of filter results';
                        }
                    } else {
                        if (isLastRecordStartOfRecords) {
                            bottomIndicatorType = 'red';
                            bottomLabel = 'Start of Records';
                        } else {
                            bottomIndicatorType = 'orange';
                            bottomLabel = 'Beginning of filter results';
                        }
                    }
                    
                    return (
                        <>
                            {/* Beginning indicator */}
                            {processedData.length > 0 && (
                                <RangeIndicator type={topIndicatorType} label={topLabel} />
                            )}
                            
                            {processedData.map((row, idx) => {
                        const style = highlightRows ? highlightRows(row, idx) : {};
                        const isCurrentDayPlaceholder = row.id === 'current-day-placeholder';
                        const rowDate = new Date(row.date);
                        rowDate.setHours(0, 0, 0, 0);
                        const isCurrentDay = isCurrentDayPlaceholder || rowDate.getTime() === today.getTime();
                        // Highlight the row that corresponds to the cashflow "first negative" date/transaction
                        const firstNegativeDateStr = companyStatus?.firstNegativeDate;
                        const firstNegativeGuid = companyStatus?.firstNegativeTransactionGuid;
                        // Prefer matching by GUID when available; fall back to matching by date only when GUID isn't provided
                        const isCashflowRow = firstNegativeGuid
                            ? row.id === firstNegativeGuid
                            : (firstNegativeDateStr && new Date(row.date).toDateString() === new Date(firstNegativeDateStr).toDateString());

                        let bgColor = 'var(--color-page-background)';

                        if (isCurrentDay) {
                            bgColor = 'var(--color-current-day-bg)';
                        } else if (isCashflowRow) {
                            bgColor = 'var(--color-run-out-of-money-bg)';
                        } else if (row.type === 'real' || row.type === null) {
                            bgColor = idx % 2 === 0 ? 'var(--color-transaction-row-bg)' : 'rgba(var(--color-transaction-row-bg-rgb), 0.5)';
                        } else {
                            bgColor = style.background || (idx % 2 === 0 ? 'var(--color-provision-row-bg)' : 'rgba(var(--color-provision-row-bg-rgb), 0.5)');
                        }
                        
                        return (
                            <Box
                                ref={isCurrentDay ? currentDayRef : null}
                                key={row.id ? `${row.id}-${idx}` : idx}
                                onClick={() => row.id !== 'current-day-placeholder' && onRowClick?.(row.id, row.date, row.bank, row.category)}
                                onMouseEnter={() => setHoveredRowId(row.id)}
                                onMouseLeave={() => setHoveredRowId(null)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    background: bgColor,
                                    color: style.color || 'var(--color-default-text)',
                                    fontWeight: isCashflowRow ? 700 : (style.fontWeight || 400),
                                    transition: 'background 0.2s',
                                    ':hover': { background: row.id === 'current-day-placeholder' ? bgColor : isCashflowRow ? '#ff4d4d7d' : 'rgba(var(--color-primary-rgb), 0.2078)' },
                                    minHeight: 40,
                                    // borderLeft: isCashflowRow ? '4px solid #A80000' : undefined,
                                boxSizing: 'border-box',
                            }}
                        >
                            {row.id === 'current-day-placeholder' ? (
                                // Render only date for placeholder rows
                                <Box sx={{ width: '100%', height: '100%', pl: 2, fontSize: 14, fontWeight: 600, textAlign: 'center' }}>
                                    {formatDate(new Date(row.date))}
                                </Box>
                            ) : (
                                <>
                                {/* Grouped columns: Date, Amount, Balance */}
                                <Box sx={{ display: 'flex', gap: 0, alignItems: 'center' }}>
                                {columns.filter(col => ['date', 'amount', 'balance'].includes(col.id)).map((col, colIdx) => {
                                    let value = (row as any)[col.id];
                                    if (col.id === 'amount') {
                                        value = (
                                            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                                <span 
                                                    style={{
                                                        color: Number(row.amount) < 0
                                                            ? 'var(--color-negative-amount)'
                                                            : 'var(--color-default-text)',
                                                        textAlign: 'right',
                                                        display: 'inline-block',
                                                        width: '80%',
                                                        fontWeight: (row.type === 'provision') 
                                                            ? row.linkedTransactionId ? 800 : 600 
                                                            : row.linkedTransactionId ? 800 : 400 }}>
                                                    {formatAmount?.(Number(row.amount))}
                                                </span>
                                            </Box>
                                        );
                                    }
                                    if (col.id === 'balance') {
                                        // only show the pseudo-element when the transaction date is today
                                        const showAfter = isCurrentDay;

                                        value = (
                                            <Box 
                                                sx={{ 
                                                    width: '100%', 
                                                    display: 'flex', 
                                                    justifyContent: 'center', 
                                                    boxSizing: 'border-box',
                                                    position: 'relative',
                                                    ...(showAfter && {
                                                        '&::after': {
                                                            content: '""',
                                                            position: 'absolute',
                                                            top: -10.25,
                                                            left: 0,
                                                            width: '100%',
                                                            background: companyStatus?.firstNegativeTransaction?.guid === row.id ? '#ff00004b' : 'inherit',
                                                            height: 'calc(100% + 20.5px)',
                                                            boxSizing: 'border-box',
                                                        }
                                                    })
                                                }}>
                                                <span
                                                    style={{
                                                        color: Number(row.balance) < 0
                                                            ? 'var(--color-negative-amount)' : 'var(--color-default-text)',
                                                        textAlign: 'right',
                                                        display: 'inline-block',
                                                        width: '80%',
                                                        paddingRight: '8px',
                                                        fontWeight: (row.type === 'provision') ? 600 : 400 }}>
                                                    {formatAmount?.(Number(row.balance))}
                                                </span>
                                            </Box>
                                        );
                                    }
                                    return (
                                        <Box
                                            key={col.id}
                                            sx={{ 
                                                width: col.width, 
                                                minWidth: col.width, 
                                                pl: col.id === 'date' || col.id === 'amount' || col.id === 'balance' ? 2 : 0, 
                                                fontSize: 14, 
                                                fontWeight: (row.type === 'provision') ? 600 : 400, 
                                                color: (row.type === 'real' || row.type === null) ? 'var(--color-transaction-text)' : 'var(--color-provision-text)',
                                                boxSizing: 'border-box',
                                                textWrap: 'nowrap', 
                                            }}
                                        >
                                            {col.id === 'date'
                                                ? (() => {
                                                    // If row.date is a string and matches a year (e.g., "2025")
                                                    if (typeof row.date === 'string' && /^\d{4}$/.test(row.date)) {
                                                        return row.date;
                                                    }
                                                    // If row.date is a string formatted like "startDate to endDate"
                                                    if (
                                                        typeof row.date === 'string' &&
                                                        row.date.includes(' to ')
                                                    ) {
                                                        const [start, end] = row.date.split(' to ');
                                                        const startDate = new Date(start);
                                                        const endDate = new Date(end);
                                                        const startFormatted = formatDate(startDate);
                                                        const endFormatted = formatDate(endDate);
                                                        
                                                        // Use arrow format for weekly view
                                                        if (viewBy === 'week') {
                                                            return `${startFormatted}⇾${endFormatted}`;
                                                        }
                                                        // For monthly view, show only month and year
                                                        if (viewBy === 'month') {
                                                            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                                            const month = monthNames[endDate.getMonth()];
                                                            const year = endDate.getFullYear();
                                                            return `${month} ${year}`;
                                                        }
                                                        // For quarterly view, show Q# and year
                                                        if (viewBy === 'quarter') {
                                                            const month = endDate.getMonth();
                                                            const quarter = Math.floor(month / 3) + 1;
                                                            const year = endDate.getFullYear();
                                                            return `Q${quarter} ${year}`;
                                                        }
                                                        // For day view, show just the date
                                                        if (viewBy === 'day') {
                                                            return formatDate(endDate);
                                                        }
                                                        // For year view, show just the year
                                                        if (viewBy === 'year') {
                                                            return String(endDate.getFullYear());
                                                        }
                                                        return `${startFormatted} to ${endFormatted}`;
                                                    }
                                                    // Otherwise, treat as a normal date string
                                                    return formatDate(new Date(row.date));
                                                })()
                                                : value}
                                        </Box>
                                    );
                                })}
                                </Box>

                                {/* Remaining columns: Description, Bank, Category, Actions */}
                                {columns.filter(col => !['date', 'amount', 'balance'].includes(col.id)).map((col, colIdx) => {
                                    let value = (row as any)[col.id];
                                    if (col.id === 'description') {
                                        value = reformatGstDescriptionDates(row.description, formatDate);
                                    } else if (col.id === 'bank') {
                                        const bankLabel = String(bankOptions.find(option => String(option.value) === String(row.bank))?.label || row.bank || '');
                                        // Extract bank name and account name from format "BankName (AccountName)"
                                        const bankMatch = bankLabel.match(/^([^(]+)\s*\(([^)]+)\)$/);
                                        const bankName = bankMatch ? bankMatch[1].trim() : '';
                                        const accountName = bankMatch ? bankMatch[2].trim() : bankLabel;
                                        
                                        value = (
                                            <Tooltip 
                                                title={bankName || bankLabel} 
                                                componentsProps={{
                                                    tooltip: {
                                                        sx: {
                                                            backgroundColor: '#fffefb',
                                                            color: '#333333',
                                                            fontSize: '14px',
                                                            border: '1px solid #e9e9e9',
                                                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                                        }
                                                    }
                                                }}
                                                arrow>
                                                <span>{accountName}</span>
                                            </Tooltip>
                                        );
                                    }
                                    if (col.id === 'category') {
                                        value = (
                                            <div onClick={(e) => e.stopPropagation()}>
                                                <TreeDropdown
                                                    enableHighlightMode
                                                    options={(hierarchicalCategories || []).filter(cat => cat.id !== 'select-all-categories')}
                                                    value={String(row.category)}
                                                    onChange={(selectedValue) => onCategoryChange?.(row.id, selectedValue ? Number(selectedValue) : null)}
                                                    placeholder="Select..."
                                                    height={24}
                                                    width={180}
                                                    borderColor="var(--color-primary)"
                                                    disabled={editDisabled}
                                                />
                                        </div>
                                    );
                                    }
                                    if (col.id === 'actions') {
                                        value = (
                                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '5px', paddingRight: '10px' }} onClick={(e) => e.stopPropagation()}>
                                                {row.linkedTransactionId &&
                                                    <Box display="flex" justifyContent="flex-end">
                                                        <Box
                                                            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
                                                        >
                                                            <Link size={14} color={(row.type === 'real' || row.type === null) ? '#1c1c1cff' : 'var(--color-primary)'}/>
                                                            <Typography fontSize={10} sx={{ color: (row.type === 'real' || row.type === null) ? '#1c1c1cff' : 'var(--color-primary)' }}>
                                                                Linked
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                }
                                                {row.provisionThisTemplateId ? (
                                                    <Box display="flex" justifyContent="flex-end">
                                                        <Tooltip 
                                                            title="Show related provision"
                                                            componentsProps={{
                                                                tooltip: {
                                                                    sx: {
                                                                        backgroundColor: '#fffefb',
                                                                        color: '#333333',
                                                                        fontSize: '14px',
                                                                        border: '1px solid #e9e9e9',
                                                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                                                    }
                                                                }
                                                            }}
                                                            arrow
                                                        >
                                                            <Box
                                                                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
                                                                onClick={()=> navigate(`/provision?isEdit=true&templateId=${row.provisionThisTemplateId}`)}
                                                            >
                                                                <SquareCheckBig size={20} color={'var(--color-primary)'}/>
                                                            </Box>
                                                        </Tooltip>
                                                    </Box>
                                                ) : (
                                                    (row.type === 'real') && (
                                                        <TransactionCheckbox
                                                            rowId={row.id}
                                                            isChecked={checkedRows.has(row.id)}
                                                            isHovered={hoveredRowId === row.id}
                                                            hasAnyChecked={checkedRows.size > 0}
                                                            onToggle={handleCheckboxToggle}
                                                        />
                                                    )
                                                )}
                                                { (row.type === 'real') &&
                                                    <ActionMenu actions={[
                                                        { label: 'Link a Provision', onClick: () => onLinkProvision && onLinkProvision(row), icon: 'link', disabled: editDisabled },
                                                        ...(!row.provisionThisTemplateId ? [{ label: 'Provision This', onClick: () => onProvisionThis && onProvisionThis(row), icon: 'paste' as const, disabled: editDisabled }] : [])
                                                    ]} />
                                                }
                                                { (row.type === 'provision') &&
                                                    <ActionMenu actions={[
                                                        { label: 'Edit', onClick: () => onEdit && onEdit(row), icon: 'edit', disabled: editDisabled },
                                                        { label: 'Delete', onClick: () => onDelete && onDelete(row), icon: 'delete', disabled: deleteDisabled }
                                                    ]} />
                                                }
                                            </div>
                                        );
                                    }
                                    return (
                                        <Box
                                            key={col.id}
                                            sx={{ 
                                                width: col.width, 
                                                minWidth: col.width, 
                                                pl: 0, 
                                                fontSize: 14, 
                                                fontWeight: (row.type === 'provision') ? 600 : 400, 
                                                color: (row.type === 'real' || row.type === null) ? 'var(--color-transaction-text)' : 'var(--color-provision-text)',
                                                boxSizing: 'border-box',
                                                textWrap: (col.id === 'description' || col.id === 'bank') ? 'wrap' : 'nowrap', 
                                            }}
                                        >
                                            {value}
                                        </Box>
                                    );
                                })}
                                </>
                            )}
                        </Box>
                    );
                            })}
                            
                            {/* Ending indicator */}
                            {processedData.length > 0 && !hasNextPage && (
                                <RangeIndicator type={bottomIndicatorType} label={bottomLabel} />
                            )}
                            
                            {/* Loading indicator for infinite scroll */}
                            {isLoadingMore && (
                                <Box 
                                    sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'center', 
                                        alignItems: 'center',
                                        padding: '20px',
                                        marginTop: '10px'
                                    }}
                                >
                                    <CircularProgress size={24} sx={{ color: 'var(--color-primary)' }} />
                                </Box>
                            )}
                        </>
                    );
                })()}
                </div>
            )}
            
            <ProvisionSelectorPopup
                open={showProvisionSelector}
                onClose={() => setShowProvisionSelector(false)}
                onSelect={(provisionId, /* description */) => {
                    const selectedGuids = Array.from(checkedRows);
                    if (onMarkTransactions) {
                        onMarkTransactions(selectedGuids, provisionId);
                    }
                    setCheckedRows(new Set());
                }}
            />
        </Paper>
    );
};

export default TransactionsTable;
